import React from 'react'
import { cn } from '@/lib/utils'

interface CharacterCounterProps {
  current: number
  max: number
  warning?: number // Threshold for warning color (percentage)
  className?: string
  showRemaining?: boolean
}

export const CharacterCounter: React.FC<CharacterCounterProps> = ({
  current,
  max,
  warning = 80,
  className,
  showRemaining = false,
}) => {
  const percentage = (current / max) * 100
  const remaining = max - current
  const isOverLimit = current > max
  const isNearLimit = percentage >= warning && percentage < 100

  // Determine color based on percentage
  const getColorClass = () => {
    if (isOverLimit) return 'text-red-600'
    if (isNearLimit) return 'text-yellow-600'
    return 'text-muted-foreground'
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className={cn('text-xs font-medium transition-colors', getColorClass())}>
        {showRemaining ? (
          <>
            {isOverLimit ? (
              <>{Math.abs(remaining)} over</>
            ) : (
              <>{remaining} left</>
            )}
          </>
        ) : (
          <>
            {current}/{max}
          </>
        )}
      </span>
      {/* Visual indicator bar */}
      <div className="relative w-16 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'absolute left-0 top-0 h-full transition-all duration-200',
            isOverLimit
              ? 'bg-red-500'
              : isNearLimit
              ? 'bg-yellow-500'
              : 'bg-green-500'
          )}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  )
}

// Enhanced counter with recommendations
interface CharacterCounterWithTipsProps extends CharacterCounterProps {
  fieldName: string
  recommendations?: string[]
}

export const CharacterCounterWithTips: React.FC<CharacterCounterWithTipsProps> = ({
  fieldName,
  recommendations = [],
  ...counterProps
}) => {
  const percentage = (counterProps.current / counterProps.max) * 100
  const isOverLimit = counterProps.current > counterProps.max
  const isNearLimit = percentage >= (counterProps.warning || 80) && percentage < 100

  const getRecommendation = () => {
    if (isOverLimit) {
      return `${fieldName} is too long. Consider shortening it for better readability.`
    }
    if (isNearLimit) {
      return `${fieldName} is approaching the recommended length.`
    }
    if (counterProps.current < counterProps.max * 0.5) {
      return `You have plenty of room to expand your ${fieldName.toLowerCase()}.`
    }
    return null
  }

  const recommendation = getRecommendation()

  return (
    <div className="space-y-1">
      <CharacterCounter {...counterProps} />
      {recommendation && (
        <p className="text-xs text-muted-foreground italic">{recommendation}</p>
      )}
      {recommendations.length > 0 && !isOverLimit && (
        <ul className="text-xs text-muted-foreground space-y-0.5">
          {recommendations.map((tip, index) => (
            <li key={index}>• {tip}</li>
          ))}
        </ul>
      )}
    </div>
  )
}